import React, { useState, useMemo } from "react";
import { MessageSquare, Share2, Award, CheckCircle, XCircle, MessageSquareReply } from "lucide-react";
import { Card, Skeleton, Button, SplitView } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Review, ReviewReplyStatus, ReviewServices, Media } from "@/services/bedrock";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { URLS, useSocialCoachSession } from "@/utils";
import { usePlayerDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { useShareFlow } from "@/components/ShareDesignPost/ShareFlowProvider";
import { ShareOnSocialCard, ShareOnSocialCardSkeleton } from "@/components/ShareDesignPost/ShareOnSocialCard";
import { SharedOnSocialSection } from "@/components/ShareDesignPost/SharedOnSocialSection";
import { useAvailableDesigns } from "@/components/ShareDesignPost/hooks/useAvailableDesigns";
import { useReviewPosts } from "@/components/ShareDesignPost/hooks/useReviewPosts";
import { getReviewSentiment, SOURCE_SYSTEM_CONFIG } from "@/components/reviews/utils";
import { socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { ReviewHeroCard, ReviewReplyDialog } from "@/components/reviews";
import { ReviewSuccessDialog } from "./ReviewSuccessDialog";
import { ReviewTextReplyCard } from "./ReviewTextReplyCard";
import { ReviewVideoReplyCard } from "./ReviewVideoReplyCard";
import { useReviewsState } from "../hooks/useReviewsState";
import { useReviewFilters } from "../ReviewFiltersContext";
import RecordVideoDialog from "@/containers/RecordVideo/RecordVideoDialog";
import { VideoPageModal } from "@/containers/VideoPages";
import { toast } from "sonner";

interface ReviewPanelProps {
  selectedReview: Review | null;
  isLoading: boolean;
  reviewsCount: number;
  onShareReviewLink?: () => void;
}

export function ReviewPanel({ selectedReview, isLoading, reviewsCount, onShareReviewLink }: ReviewPanelProps) {
  const history = useHistory();
  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [replyDialogMode, setReplyDialogMode] = useState<"video" | "text">("video");
  const [updatingReview, setUpdatingReview] = useState(false);

  // Inline recording and video page state variables
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [scriptToRecord, setScriptToRecord] = useState("");
  const [recordedMedia, setRecordedMedia] = useState<Media | null>(null);
  const [showVideoPageModal, setShowVideoPageModal] = useState(false);
  const [viewVideoPageId, setViewVideoPageId] = useState<number | null>(null);
  const [autoOpenShareModal, setAutoOpenShareModal] = useState(false);

  // Success Celebration Modal state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successType, setSuccessType] = useState<"video" | "text">("video");

  const { invalidateReviews, updateReview, isUpdatingReview } = useReviewsState();
  const { markReviewAsCompleted, dismissCompletedReview, setTriageFilter } = useReviewFilters();

  // Query to fetch the full review details including 'latest_content_page' and 'review_replies'
  const {
    data: fullReview,
    isLoading: isLoadingReviewDetails,
    isError: reviewDetailsFailed,
  } = useQuery({
    queryKey: ["review-details", selectedReview?.id],
    queryFn: () => {
      if (!selectedReview) return null;
      return ReviewServices.getReview(selectedReview.id, {
        extraFields: ["latest_content_page", "review_replies", "social_identity", "form"],
      });
    },
    enabled: !!selectedReview?.id,
  });

  // A review restored from ?review= is only an id until its details load.
  const selectedIsPlaceholder = !!selectedReview && !selectedReview.guid;
  const activeReview = fullReview || (selectedIsPlaceholder ? null : selectedReview);

  const { playerId } = useSocialCoachSession();
  const shareFlow = useShareFlow();
  const dynamicAccess = usePlayerDynamicPromptsAccess(playerId);
  const canShare = dynamicAccess === true;
  const { data: availableDesigns, isLoading: designsLoading } = useAvailableDesigns(canShare ? playerId : null);
  const { data: reviewPosts, isLoading: reviewPostsLoading } = useReviewPosts(canShare ? activeReview?.id : null);

  const videoPageModalInitialObject = useMemo(() => {
    if (recordedMedia) return { media: recordedMedia };
    if (viewVideoPageId) return { videoPageId: viewVideoPageId };
    return undefined;
  }, [recordedMedia, viewVideoPageId]);

  // AI text generation query
  const { data: generatedTextData, isLoading: isGeneratingText } = useQuery({
    queryKey: ["reviews-text-generation", activeReview?.id],
    queryFn: () => {
      if (!activeReview) {
        return null;
      }
      return ReviewServices.generateResponseText(activeReview.id, {});
    },
    enabled: !!activeReview?.id,
  });

  // AI script generation query
  const { data: generatedScriptData, isLoading: isGeneratingScript } = useQuery({
    queryKey: ["reviews-script-generation", activeReview?.id],
    queryFn: () => {
      if (!activeReview) {
        return null;
      }
      return ReviewServices.generateResponseScript(activeReview.id, {});
    },
    enabled: !!activeReview?.id,
  });
  const generatedText = generatedTextData?.response || "";
  const generatedScript = generatedScriptData?.script || "";

  const handleContinueToTeleprompter = (script: string) => {
    setShowReplyDialog(false);
    if (!activeReview) {
      return;
    }

    setScriptToRecord(script);
    setShowRecordDialog(true);
  };

  const handleViewExistingResponse = (videoPageId: number) => {
    setViewVideoPageId(videoPageId);
    setAutoOpenShareModal(true);
    setShowVideoPageModal(true);
  };

  const featureReview = () => {
    setUpdatingReview(true);
    ReviewServices.featureReview(activeReview?.id!)
      .then(() => {
        toast.success("Review featured successfully!");
        invalidateReviews();
      })
      .catch(error => {
        console.error("Error updating review:", error);
        toast.error("Failed to update review. Please try again.");
      })
      .finally(() => setUpdatingReview(false));
  };

  const updateReplyStatus = async (replyStatus: ReviewReplyStatus) => {
    if (!activeReview?.id) return;
    try {
      await updateReview({ reviewId: activeReview.id, replyStatus });
    } catch (e) {
      console.error(e);
    }
  };

  const sentiment = activeReview ? getReviewSentiment(activeReview) : "neutral";
  const platformName = activeReview
    ? socialNetworkNames[activeReview.sourceSystem as SocialNetworksId] || activeReview.sourceSystem
    : "Platform";
  const isInternalFeedback = !!(
    activeReview?.sourceSystem && SOURCE_SYSTEM_CONFIG[activeReview?.sourceSystem]?.isInternal
  );

  const hasExistingReplies = !activeReview?.reviewReplies || activeReview.reviewReplies.length > 0;
  const replyFirst = !hasExistingReplies && !isInternalFeedback;

  const openShare = () => activeReview && shareFlow.open({ kind: "review", reviewId: Number(activeReview.id) });

  const shareable = activeReview?.postable !== false;

  const renderShareElement = (): React.ReactNode => {
    if (!canShare || !activeReview) return null;
    if (!designsLoading && !availableDesigns?.some(design => design.contextType === "review")) return null;
    if (designsLoading || reviewPostsLoading) return <ShareOnSocialCardSkeleton />;
    if (reviewPosts && reviewPosts.length > 0) {
      return (
        <SharedOnSocialSection
          reviewId={Number(activeReview.id)}
          posts={reviewPosts}
          canShareAgain={shareable}
          onShareAgain={openShare}
          onOpenPost={(postId, socialNetworkId) => shareFlow.openPost(postId, socialNetworkId ?? undefined)}
        />
      );
    }
    if (!shareable) {
      return (
        <ShareOnSocialCard
          variant="muted"
          reasons={activeReview.postabilityReasons}
          reviewerName={activeReview.displayName}
          onShare={openShare}
        />
      );
    }
    return <ShareOnSocialCard variant="ready" onShare={openShare} />;
  };
  const shareContent = renderShareElement();
  const shareElement = shareContent ? <div className="mt-2">{shareContent}</div> : null;
  // A card the player can't act on goes last, after the ways they can respond.
  const shareGoesLast = !!activeReview && activeReview.postable === false && !reviewPosts?.length;

  return (
    <SplitView.Detail bodyClassName="p-0 gap-0 overflow-hidden">
      {isLoading ||
      (reviewsCount > 0 && !activeReview && !(selectedIsPlaceholder && reviewDetailsFailed)) ||
      isLoadingReviewDetails ? (
        <div className="flex-1 overflow-y-auto flex flex-col gap-6 min-h-0 p-4">
          {/* Part 1: Hero review metadata skeleton inside Card */}
          <Card className="p-6 space-y-4 border border-slate-200 rounded-2xl bg-white shadow-sm shrink-0 animate-pulse">
            <div className="flex items-start gap-4">
              <Skeleton className="size-12 rounded-full shrink-0" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-6 w-48 rounded" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-24 rounded" />
                  <span className="text-xs text-slate-300">•</span>
                  <Skeleton className="h-4 w-16 rounded" />
                  <span className="text-xs text-slate-300">•</span>
                  <Skeleton className="h-4 w-28 rounded" />
                </div>
              </div>
            </div>
            <Skeleton className="h-16 w-full rounded-xl" />
          </Card>

          {/* Part 2: Reply Cards Skeletons */}
          <div className="flex flex-col gap-2 shrink-0 animate-pulse">
            <div className="space-y-1 shrink-0 mt-2 mb-1">
              <Skeleton className="h-4 w-40 rounded" />
            </div>

            {/* SKELETON A: Hero Text Reply Card */}
            <Card className="flex flex-col pt-10 pb-6 px-6 md:pt-12 md:pb-8 md:px-8 border-2 border-slate-100 bg-white rounded-2xl relative shadow-sm">
              <div className="absolute top-0 left-0 right-0 h-8 border-b-2 border-slate-50 rounded-t-2xl bg-slate-50/50" />
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-start gap-4">
                  <Skeleton className="size-14 rounded-2xl shrink-0 hidden sm:flex" />
                  <div className="flex flex-col gap-3">
                    <Skeleton className="h-7 w-48 rounded" />
                    <Skeleton className="h-5 w-72 rounded" />
                  </div>
                </div>
                <Skeleton className="w-full md:w-36 h-12 rounded-xl" />
              </div>
            </Card>

            {/* SKELETON B: Secondary Video Reply Card */}
            <div className="mt-2">
              <Card className="flex flex-col p-8 md:p-10 border border-slate-200 bg-white rounded-2xl relative shadow-sm">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <Skeleton className="size-14 rounded-2xl shrink-0 hidden sm:flex" />
                    <div className="flex flex-col gap-3">
                      <Skeleton className="h-7 w-56 rounded" />
                      <Skeleton className="h-5 w-64 rounded" />
                    </div>
                  </div>
                  <Skeleton className="w-full md:w-56 h-12 rounded-xl" />
                </div>
              </Card>
            </div>

            {/* SKELETON QUICK ACTIONS */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4 border-t border-slate-100 pt-5">
              <Skeleton className="h-9 w-32 rounded-lg" />
              <Skeleton className="h-9 w-32 rounded-lg" />
              <Skeleton className="h-9 w-32 rounded-lg" />
            </div>
          </div>
        </div>
      ) : activeReview ? (
        <div className="flex-1 overflow-y-auto flex flex-col gap-6 min-h-0 p-4 pb-12">
          {/* Part 1: Hero review card metadata inside Card */}
          <ReviewHeroCard review={activeReview} />

          {/* Part 2: Interactive AI Reply selection */}
          <div className="flex flex-col gap-2 shrink-0 select-none">
            <div className="space-y-1 shrink-0 mt-2 mb-1">
              <div className="text-sm font-bold uppercase tracking-wider text-slate-500">
                {activeReview.replied ? "Response Tools & Actions" : "Choose how to respond"}
              </div>
            </div>

            {/* Share first unless a reply is still owed */}
            {!replyFirst && !shareGoesLast && shareElement}

            {/* OPTION A: STANDARD TEXT REPLY (PRIMARY EMPHASIS) - Only shown if no replies yet */}
            {replyFirst && (
              <ReviewTextReplyCard
                platformName={platformName}
                isInternalFeedback={isInternalFeedback}
                sourceSystem={activeReview?.sourceSystem}
                sentiment={sentiment}
                onOpenDialog={() => {
                  setReplyDialogMode("text");
                  setShowReplyDialog(true);
                }}
              />
            )}

            {/* OPTION B: REPLY WITH VIDEO (ENHANCEMENT) */}
            <div className={cn(replyFirst && "mt-2")}>
              <ReviewVideoReplyCard
                review={activeReview}
                sentiment={sentiment}
                onRecordResponse={() => {
                  setReplyDialogMode("video");
                  setShowReplyDialog(true);
                }}
                onViewExistingResponse={handleViewExistingResponse}
                isLoadingDetails={isLoadingReviewDetails}
              />
            </div>

            {(replyFirst || shareGoesLast) && shareElement}

            {/* QUICK ACTIONS ROW (TRIAGE) */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-2 border-t border-slate-200 pt-5">
              {activeReview.aiShareabilityScore && activeReview.aiShareabilityScore == 100 ? (
                <div className="w-full sm:w-auto h-9 font-semibold text-xs text-slate-600 flex flex-row items-center px-2 gap-1.5">
                  <Award className="size-3.5 text-amber-500" />
                  Review Featured!
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full sm:w-auto h-9 font-semibold text-xs text-slate-600 border-slate-200 shadow-sm hover:bg-slate-100 hover:text-slate-900 gap-1.5"
                  onClick={featureReview}
                  disabled={updatingReview || isUpdatingReview}
                >
                  <Award className="size-3.5 text-amber-500" />
                  Feature Review
                </Button>
              )}

              {hasExistingReplies && !isInternalFeedback && (
                <Button
                  variant="outline"
                  className="w-full sm:w-auto h-9 font-semibold text-xs text-slate-600 border-slate-200 shadow-sm hover:bg-slate-100 hover:text-slate-900 gap-1.5"
                  onClick={() => {
                    setReplyDialogMode("text");
                    setShowReplyDialog(true);
                  }}
                  disabled={updatingReview || isUpdatingReview}
                >
                  <MessageSquareReply className="size-3.5 text-slate-500" />
                  Post Another Reply
                </Button>
              )}

              {!activeReview.replied && (
                <>
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto h-9 font-semibold text-xs text-slate-600 border-slate-200 shadow-sm hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 gap-1.5"
                    disabled={updatingReview || isUpdatingReview}
                    onClick={() => updateReplyStatus("replied")}
                  >
                    <CheckCircle className="size-3.5 text-emerald-500" />
                    Mark as Replied
                  </Button>
                  {!activeReview?.replySkipped && (
                    <Button
                      variant="ghost"
                      className="w-full sm:w-auto h-9 font-semibold text-xs text-slate-500 hover:bg-rose-50 hover:text-rose-700 gap-1.5"
                      disabled={updatingReview || isUpdatingReview}
                      onClick={() => updateReplyStatus("skipped")}
                    >
                      <XCircle className="size-3.5" />
                      Skip Review
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : reviewsCount === 0 ? (
        // Beautiful Zero-State Screen if there are no reviews at all
        <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50/50">
          <Card className="flex flex-col items-center max-w-sm p-10 border border-slate-200 bg-white rounded-2xl shadow-sm text-center">
            <div className="bg-indigo-50 text-primary p-3.5 rounded-full mb-4 shrink-0 shadow-inner">
              <MessageSquare className="h-6 w-6" />
            </div>
            <h3 className="font-extrabold text-slate-800 text-lg mb-2">Build Your Online Reputation</h3>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              Connect your professional profiles to automatically import reviews, or share your custom form link to
              start collecting new customer feedback.
            </p>
            <div className="flex flex-col gap-3 w-full shrink-0">
              <Button
                variant="default"
                onClick={onShareReviewLink}
                className="w-full h-10 font-bold flex items-center justify-center gap-2 shadow-sm"
              >
                <Share2 className="size-4 text-current" />
                Share Review Link
              </Button>
              <Button
                variant="outline"
                onClick={() => history.push(URLS.player.settingsSocialNetworks)}
                className="w-full h-10 border-slate-200 hover:bg-slate-50 text-slate-700 font-bold flex items-center justify-center gap-2"
              >
                Connect Platforms
              </Button>
            </div>
          </Card>
        </div>
      ) : (
        // Fallback screen if there are reviews but none is selected for some reason
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-slate-400">
          <Card className="flex flex-col items-center justify-center text-center p-12 border border-slate-200 bg-white rounded-2xl shadow-sm max-w-sm">
            <MessageSquare className="h-10 w-10 text-slate-300 mb-3" />
            <h4 className="font-bold text-slate-700 mb-1">Select a review</h4>
            <p className="text-sm text-slate-500 leading-relaxed">
              Click a review card from the left panel to display details and generate premium AI replies.
            </p>
          </Card>
        </div>
      )}

      {/* Unified Reply Dialog (Script/Text responding & refinement) */}
      {showReplyDialog && activeReview && (
        <ReviewReplyDialog
          open={showReplyDialog}
          onOpenChange={setShowReplyDialog}
          review={activeReview}
          generatedText={replyDialogMode === "video" ? generatedScript : generatedText}
          isGenerating={replyDialogMode === "video" ? isGeneratingScript : isGeneratingText}
          mode={replyDialogMode}
          onContinueToTeleprompter={handleContinueToTeleprompter}
          onTextReplyComplete={() => {
            // Upon completing text reply, mark as completed in session and trigger success dialog
            if (activeReview?.id && selectedReview && !selectedReview.replied) {
              markReviewAsCompleted(activeReview.id);
              setSuccessType("text");
              setShowSuccessDialog(true);
            } else {
              invalidateReviews();
            }
          }}
        />
      )}

      {/* Inline Recording Dialog */}
      {showRecordDialog && (
        <RecordVideoDialog
          open={showRecordDialog}
          initialScript={scriptToRecord}
          initialRecordVideoType="teleprompter"
          onOpenChange={setShowRecordDialog}
          onDone={(media: Media) => {
            setShowRecordDialog(false);
            setRecordedMedia(media);
            setAutoOpenShareModal(true);
            setShowVideoPageModal(true);
          }}
        />
      )}

      {/* Inline Video Page Creation Modal */}
      {showVideoPageModal && videoPageModalInitialObject && (
        <VideoPageModal
          open={showVideoPageModal}
          initialObject={videoPageModalInitialObject}
          reviewId={selectedReview?.id}
          autoOpenShare={autoOpenShareModal}
          onClose={() => {
            setShowVideoPageModal(false);
            setRecordedMedia(null);
            setViewVideoPageId(null);
            setAutoOpenShareModal(false);
            if (activeReview?.id && selectedReview && !selectedReview.replied) {
              markReviewAsCompleted(activeReview.id);
              setSuccessType("video");
              setShowSuccessDialog(true);
            } else {
              invalidateReviews();
            }
          }}
        />
      )}

      {/* Success Celebration Dialog */}
      {showSuccessDialog && activeReview && (
        <ReviewSuccessDialog
          open={showSuccessDialog}
          onOpenChange={setShowSuccessDialog}
          type={successType}
          onAddVideo={() => {
            setShowSuccessDialog(false);
            setReplyDialogMode("video");
            setShowReplyDialog(true);
          }}
          onNextReview={() => {
            dismissCompletedReview(activeReview.id);
            invalidateReviews();
          }}
          onDismiss={() => {
            dismissCompletedReview(activeReview.id);
            setTriageFilter(null); // Clear unreplied filter so it stays in view!
            invalidateReviews();
          }}
        />
      )}
    </SplitView.Detail>
  );
}
